import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileAlt,
  FaFileImage,
  FaFileVideo,
  FaSpinner,
  FaWhatsapp,
  FaPlus,
  FaInfoCircle,
} from "react-icons/fa";
import "./TemplateList.css";

function WhatsappTemplateList() {
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const navigate = useNavigate();

  const tenantName = localStorage.getItem("tenantName");

  // Fetch WhatsApp Templates
  useEffect(() => {
    const fetchWhatsappTemplates = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/templates/templates`,
          { headers: { "x-email": localStorage.getItem("tenantEmail") } }
        );
        const templateData = response.data.data.map((item) => ({
          _id: item.id,
          whatsappTemplateId: item.id,
          name: item.name,
          components: item.components,
          language: item.language,
          category: item.category,
          status: item.status,
          createdAt: new Date(item.quality_score.date * 1000).toISOString(),
        }));
        setWhatsappTemplates(templateData);
        setError("");
      } catch (err) {
        console.error(
          "Error fetching WhatsApp templates:",
          err.response?.data || err.message
        );
        setError("Failed to fetch WhatsApp templates. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchWhatsappTemplates();
  }, []);

  const getFileIcon = (format) => {
    switch (format) {
      case "DOCUMENT":
        return <FaFileWord className="text-primary me-2" />;
      case "PDF":
        return <FaFilePdf className="text-danger me-2" />;
      case "EXCEL":
        return <FaFileExcel className="text-success me-2" />;
      case "IMAGE":
        return <FaFileImage className="text-info me-2" />;
      case "VIDEO":
        return <FaFileVideo className="text-warning me-2" />;
      default:
        return <FaFileAlt className="text-secondary me-2" />;
    }
  };

  const renderHeader = (header) => {
    if (!header) return null;

    switch (header.format) {
      case "TEXT":
        return (
          <h6 className="mb-2" style={{ fontWeight: "bold" }}>
            {header.text || "Header Text"}
          </h6>
        );
      case "IMAGE":
        return (
          <img
            src={
              header.example?.header_handle[0] ||
              "https://via.placeholder.com/300x100"
            }
            alt="Header"
            style={{ width: "100%", borderRadius: "8px", marginBottom: "8px" }}
          />
        );
      case "DOCUMENT":
      case "PDF":
      case "VIDEO":
        return (
          <div
            className="d-flex align-items-center p-2 mb-2"
            style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}
          >
            {getFileIcon(header.format)}
            <span>{`${header.format.toLowerCase()} file`}</span>
          </div>
        );
      default:
        return null;
    }
  };

  const renderButtons = (buttons) => {
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className="mt-3">
        {buttons.map((button, index) => {
          const containerStyle = {
            display: "flex",
            flexDirection: "column",
            marginBottom: "8px",
          };

          const buttonStyle = {
            backgroundColor: "#ffffff",
            color: "#027eb5",
            border: "1px solid #027eb5",
            borderRadius: "4px",
            padding: "6px 12px",
            fontSize: "14px",
            fontWeight: "normal",
            textAlign: "left",
            width: "100%",
            maxWidth: "250px",
            cursor: "pointer",
            marginBottom: "4px",
            textDecoration: "none",
            display: "inline-block",
          };

          switch (button.type) {
            case "URL":
              return (
                <div key={index} style={containerStyle}>
                  <a
                    href={button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={buttonStyle}
                  >
                    {button.text}
                  </a>
                </div>
              );

            case "PHONE_NUMBER":
              return (
                <div key={index} style={containerStyle}>
                  <a href={`tel:${button.phone_number}`} style={buttonStyle}>
                    {button.text}
                  </a>
                </div>
              );

            case "COPY_CODE":
              return (
                <div key={index} style={containerStyle}>
                  <button
                    style={buttonStyle}
                    onClick={() => navigator.clipboard.writeText(button.text)}
                  >
                    {button.text}
                  </button>
                </div>
              );

            case "QUICK_REPLY":
            default:
              return (
                <div key={index} style={containerStyle}>
                  <span style={buttonStyle}>{button.text}</span>
                </div>
              );
          }
        })}
      </div>
    );
  };

  const renderTemplateCard = (template) => {
    const header = template.components.find((comp) => comp.type === "HEADER");
    const body = template.components.find((comp) => comp.type === "BODY");
    const footer = template.components.find((comp) => comp.type === "FOOTER");
    const buttons =
      template.components.find((comp) => comp.type === "BUTTONS")?.buttons ||
      [];

    return (
      <div key={template._id} className="cx-template-list-card">
        <div className="cx-template-list-card-header">
          <FaWhatsapp />
          <div>
            <h4>{template.name}</h4>
            <p>Template preview</p>
          </div>
        </div>

        <div className="cx-template-list-preview">
          <div className="cx-template-list-bubble">
            {renderHeader(header)}
            {body && (
              <p className="cx-template-list-body">
                {body.text || "No content"}
              </p>
            )}
            {footer && (
              <small className="cx-template-list-footer">{footer.text}</small>
            )}
            {renderButtons(buttons)}
          </div>
        </div>

        <div className="cx-template-list-meta">
          <div className="cx-template-list-tags">
            <span className="cx-template-list-tag category">
              {template.category}
            </span>
            <span
              className={`cx-template-list-tag status ${template.status.toLowerCase()}`}
            >
              {template.status}
            </span>
          </div>
          <div className="cx-template-list-info">
            <span>Language</span>
            <strong>{template.language.toUpperCase()}</strong>
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setSelectedTemplate(template)}
          >
            <FaInfoCircle className="me-1" /> Details
          </button>
        </div>
      </div>
    );
  };

  // Loading skeleton component
  const renderSkeleton = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="cx-template-list-card skeleton-card">
        <div className="skeleton skeleton-header"></div>
        <div className="skeleton skeleton-body"></div>
        <div className="skeleton skeleton-footer"></div>
      </div>
    ));
  };

  const statuses = useMemo(
    () =>
      Array.from(
        new Set(whatsappTemplates.map((t) => t.status || "PENDING"))
      ),
    [whatsappTemplates]
  );

  const categories = useMemo(
    () =>
      Array.from(
        new Set(whatsappTemplates.map((t) => t.category || "UTILITY"))
      ),
    [whatsappTemplates]
  );

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return whatsappTemplates.filter((template) => {
      const matchesSearch = normalizedSearch
        ? `${template.name} ${template.category} ${template.status}`
            .toLowerCase()
            .includes(normalizedSearch)
        : true;
      const matchesStatus =
        statusFilter === "all" || template.status === statusFilter;
      const matchesCategory =
        categoryFilter === "all" || template.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [whatsappTemplates, searchTerm, statusFilter, categoryFilter]);

  return (
    <div className="cx-template-list-page">
      <div className="cx-template-list-hero">
        <div>
          <h1>WhatsApp Templates</h1>
          <p>Manage approved and pending templates from your workspace.</p>
        </div>
        <button
          className="btn btn-success d-flex align-items-center"
          onClick={() => navigate(`/${tenantName}/templateForm`)}
        >
          <FaPlus className="me-2" /> Create Template
        </button>
      </div>

      <div className="cx-template-list-filters">
        <div className="cx-template-list-search">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, category, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="cx-template-list-selects">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <select
            className="form-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="cx-template-list-error" role="alert">
          {error}
        </div>
      )}

      <div className="cx-template-list-grid">
        {loading ? (
          renderSkeleton()
        ) : filteredTemplates.length > 0 ? (
          filteredTemplates.map(renderTemplateCard)
        ) : (
          <div className="cx-template-list-empty">
            <FaWhatsapp size={48} />
            <h4>No templates found</h4>
            <p>Try adjusting your filters or create a new template.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/${tenantName}/templateForm`)}
            >
              <FaPlus className="me-2" /> Create Template
            </button>
          </div>
        )}
      </div>

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <div className="cx-template-list-modal-overlay">
          <div className="cx-template-list-modal">
            <div className="cx-template-list-modal-header">
              <h3>
                <FaWhatsapp /> {selectedTemplate.name}
              </h3>
              <button
                type="button"
                className="btn-close"
                onClick={() => setSelectedTemplate(null)}
              ></button>
            </div>

            <div className="cx-template-list-modal-body">
              <div className="cx-template-list-modal-summary">
                <span className="cx-template-list-tag category">
                  {selectedTemplate.category}
                </span>
                <span
                  className={`cx-template-list-tag status ${selectedTemplate.status.toLowerCase()}`}
                >
                  {selectedTemplate.status}
                </span>
                <span className="cx-template-list-modal-language">
                  {selectedTemplate.language.toUpperCase()}
                </span>
              </div>

              <div className="cx-template-list-components">
                {selectedTemplate.components.map((component, index) => (
                  <div key={index} className="cx-template-list-component">
                    <h6>{component.type}</h6>
                    {component.type === "HEADER" && (
                      <div>
                        <p>
                          <strong>Format:</strong> {component.format}
                        </p>
                        {component.text && (
                          <p>
                            <strong>Text:</strong> {component.text}
                          </p>
                        )}
                      </div>
                    )}
                    {component.type === "BODY" && (
                      <p>{component.text || "No content"}</p>
                    )}
                    {component.type === "FOOTER" && <p>{component.text}</p>}
                    {component.type === "BUTTONS" &&
                      component.buttons.length > 0 && (
                        <div className="cx-template-list-buttons">
                          {component.buttons.map((button, i) => (
                            <div key={i} className="cx-template-list-button">
                              <span>{button.text}</span>
                              <small>{button.type}</small>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>

            <div className="cx-template-list-modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelectedTemplate(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsappTemplateList;
